import EmptyState from '@/components/empty-state';
import PageHeader from '@/components/page-header';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate, formatMoney } from '@/lib/format';
import InvoiceStatusPill from '@/pages/apps/components/invoice-status-pill';
import { app_connections_path, app_invoice_path, new_app_invoice_path } from '@/rails/routes';
import type { AppInvoice, AppShell, Connection } from '@/types/platform';
import { Link } from '@inertiajs/react';

interface InvoicesIndexProps {
  app: AppShell;
  invoices: AppInvoice[];
  paymentConnection: Connection | null;
  booksConnection: Connection | null;
}

const BOOKS_STATES: Record<AppInvoice['booksState'], string> = {
  not_sent: '—',
  invoice_sent: 'In QuickBooks',
  payment_sent: 'Payment in QuickBooks',
};

const InvoicesIndex = ({ app, invoices, paymentConnection }: InvoicesIndexProps) => {
  const newButton = (
    <Button asChild>
      <Link href={new_app_invoice_path(app.id)}>New invoice</Link>
    </Button>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Invoices"
        description="Bill a customer, send the invoice, and see when it is paid."
        actions={newButton}
      />

      {paymentConnection ? null : (
        <Alert>
          <AlertDescription>
            <p>
              No payment account is connected, so invoices go out without a pay link.{' '}
              <Link
                href={app_connections_path(app.id)}
                className="text-foreground font-medium underline"
              >
                Connect a payment account
              </Link>
            </p>
          </AlertDescription>
        </Alert>
      )}

      <Card className="min-w-0">
        <CardContent>
          {invoices.length === 0 ? (
            <EmptyState
              title="No invoices yet"
              description="Make one here, or start from a record that has a customer and an amount."
              action={newButton}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>Books</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-medium">
                      <Link href={app_invoice_path(app.id, invoice.id)} className="underline">
                        {invoice.number ?? 'Draft'}
                      </Link>
                    </TableCell>
                    <TableCell className="max-w-56 truncate">
                      {invoice.customer?.name ?? <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <InvoiceStatusPill status={invoice.status} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(invoice.totalCents, invoice.currency)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {invoice.sentAt ? formatDate(invoice.sentAt) : '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {BOOKS_STATES[invoice.booksState]}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InvoicesIndex;
